import {uniq} from 'lodash';
import {getFieldNameKey, extractDynamicFieldOptions} from './utils';

const queryString = require('query-string');

/**
 * Parses URL query string into parameters object
 * @returns {Object} Parsed URL parameters
 */
export const parseUrlParameters = () => {
  return queryString.parse(window.location.search, {
    arrayFormat: 'bracket',
    parseBooleans: true,
  });
};

/**
 * Updates browser URL with new parameters
 * @param {Object} urlParameters - Parameters object to stringify
 */
export const updateBrowserUrl = (urlParameters) => {
  const newUrl = queryString.stringify(urlParameters, {arrayFormat: 'bracket'});
  window.history.pushState(newUrl, '', `/?${newUrl}`);
};

/**
 * Builds URL parameters for a dynamic filter
 * @param {Object} filter - Dynamic filter configuration
 * @param {Object} state - Component state object
 * @param {Object} urlParametersMap - URL parameter mapping config
 * @param {Object} newUrlParameters - URL parameters object to update
 */
const buildDynamicFilterUrlParams = (filter, state, urlParametersMap, newUrlParameters) => {
  const fieldName = filter.fieldName;
  const fieldNameKey = getFieldNameKey(fieldName);
  const urlAlias = (urlParametersMap[fieldNameKey] && urlParametersMap[fieldNameKey].alias) ? urlParametersMap[fieldNameKey].alias : fieldNameKey;

  if (state.selectedDynamicFilters) {
    const selectedValues = state.selectedDynamicFilters[fieldNameKey];

    if (selectedValues && Array.isArray(selectedValues) && selectedValues.length > 0) {
      newUrlParameters[urlAlias] = uniq(
        selectedValues.map((x) => {
          return x.id;
        })
      );
    } else {
      // Remove dynamic filter from URL if it's empty or not selected
      delete newUrlParameters[urlAlias];
    }
  } else {
    // Remove dynamic filter from URL if selectedDynamicFilters doesn't exist
    delete newUrlParameters[urlAlias];
  }
};

/**
 * Returns an object containing the new URL parameters.
 *
 * @param {Object} state - Component state object
 * @param {Array} filterLayout - Filter configuration from settings
 * @param {Object} urlParametersMap - URL parameter mapping config
 * @returns {Object} New URL parameters object
 */
export const buildUrlParameters = (state, filterLayout, urlParametersMap) => {
  let newUrlParameters = state.urlParameters || {};

  for (let filter of filterLayout) {
    // Handle dynamic filters
    if (filter.filter === 'dynamic') {
      buildDynamicFilterUrlParams(filter, state, urlParametersMap, newUrlParameters);
      continue;
    }

    const selectedFilterString = (filter.filter === 'plan') ? 'selectedPlanTypes' : `selected${filter.filter.charAt(0).toUpperCase() + filter.filter.slice(1)}s`;

    const selectedFilter = state[selectedFilterString];

    // Skip if selectedFilter is undefined or not an array
    if (!selectedFilter || !Array.isArray(selectedFilter)) {
      continue;
    }

    const urlAlias = (urlParametersMap[filter.filter] && urlParametersMap[filter.filter].alias) ? urlParametersMap[filter.filter].alias : filter.filter;

    newUrlParameters[urlAlias] = uniq(
      selectedFilter.map((x) => {
        return x.id;
      })
    );
  }

  return newUrlParameters;
};

/**
 * Returns valid items and a formatted filter string based on the provided filter and parameters.
 *
 * @param {Array} parameters - The list of IDs to validate.
 * @param {string} filter - The filter type (e.g., 'state', 'plan').
 * @param {Object} props - The props object containing data arrays (e.g., 'usStates', 'planTypes').
 * @returns {Object} An object containing:
 *   - {Array} validItems: Filtered, non-duplicated items.
 *   - {string} filterString: Capitalized filter name (e.g., 'PlanType', 'State').
 */
export const getValidItemsFromUrl = (parameters, filter, props) => {
  const filterPropString = (filter === 'state')
    ? 'usStates'
    : (filter === 'plan')
      ? 'planTypes'
      : `${filter}s`;

  // Map over the parameters to find matching items in the props
  const mappedItems = parameters.map((x) => {
    return props[filterPropString].find((y) => y.id == x);
  }).filter(Boolean); // Remove any falsy values

  // Map over the parameters to find matching items in the props and filter out invalid ones
  const validItems = Array.from(new Set(mappedItems));

  // Determine the filter string to use for the output
  const filterString = (filter === 'plan')
    ? 'PlanType'
    : filter.charAt(0).toUpperCase() + filter.slice(1);

  return {validItems, filterString};
};

/**
 * Parses dynamic filter values from URL parameters
 * @param {Object} filter - Dynamic filter configuration
 * @param {Object} urlParameters - URL parameters object
 * @param {Object} props - Component props object with settings
 * @param {Array} coveragePositions - Coverage positions array for validation
 * @returns {Array|null} Array of valid filter items or null if not found/invalid
 */
const parseDynamicFilterFromUrl = (filter, urlParameters, props, coveragePositions) => {
  if (!coveragePositions || !Array.isArray(coveragePositions) || coveragePositions.length === 0) {
    return null;
  }

  const fieldName = filter.fieldName;
  const fieldNameKey = getFieldNameKey(fieldName);

  // Try multiple URL parameter key variations to handle different orderings
  const possibleKeys = [
    fieldNameKey, // Normalized key (e.g., "pbmClient|payer")
    fieldName,    // Original fieldName (e.g., "payer|pbmClient")
  ];

  // Also check if urlParametersMap has aliases
  if (props.settings.urlParametersMap[fieldNameKey]?.alias) {
    possibleKeys.unshift(props.settings.urlParametersMap[fieldNameKey].alias);
  }
  if (props.settings.urlParametersMap[fieldName]?.alias && fieldName !== fieldNameKey) {
    possibleKeys.push(props.settings.urlParametersMap[fieldName].alias);
  }

  // Try each possible key until we find parameters
  let parameters = null;
  for (const key of possibleKeys) {
    if (urlParameters && typeof urlParameters === 'object' && urlParameters !== null && Object.prototype.hasOwnProperty.call(urlParameters, key)) {
      parameters = urlParameters[key];
      break;
    }
  }

  if (!parameters) {
    return null;
  }

  // Normalize parameters to array (query-string may return string for single values)
  const parameterArray = Array.isArray(parameters) ? parameters : [parameters];

  // Get valid options from coverage positions
  const options = extractDynamicFieldOptions(coveragePositions, fieldName);
  // Match parameter IDs against option IDs
  const validItems = parameterArray
    .map((id) => options.find((opt) => opt.id == id))
    .filter(Boolean);

  return validItems.length > 0 ? validItems : null;
};

/**
 * Returns selected filter values from URL parameters (dropdowns only).
 * Separated from results flag logic which stays in parent component.
 *
 * @param {Object} urlParameters - URL parameters object
 * @param {Array} filterLayout - Filter configuration from settings
 * @param {Object} props - Component props object with filter data
 * @param {Array} coveragePositions - Coverage positions array for dynamic filter validation (optional)
 * @returns {Object} Object containing selectedX arrays (e.g., {selectedPayers: [...], selectedStates: [...]}) and selectedDynamicFilters
 */
export const getSelectedFiltersFromUrl = (urlParameters, filterLayout, props, coveragePositions) => {
  let selectedFilters = {};
  let selectedDynamicFilters = {};

  for (let filter of filterLayout) {
    // Handle dynamic filters
    if (filter.filter === 'dynamic') {
      const fieldName = filter.fieldName;
      const fieldNameKey = getFieldNameKey(fieldName);
      const validItems = parseDynamicFilterFromUrl(filter, urlParameters, props, coveragePositions);

      if (validItems) {
        selectedDynamicFilters[fieldNameKey] = validItems;
      }
      continue;
    }

    const filterAlias = props.settings.urlParametersMap[filter.filter]?.alias || filter.filter;
    const parameters = urlParameters ? urlParameters[filterAlias] : null;

    if (!parameters) {
      continue; // Skip if no URL params for this filter
    }

    // Ensure filter data is loaded before processing
    const filterPropString = (filter.filter === 'state')
      ? 'usStates'
      : (filter.filter === 'plan')
        ? 'planTypes'
        : `${filter.filter}s`;

    if (!props[filterPropString] || !Array.isArray(props[filterPropString]) || props[filterPropString].length === 0) {
      continue; // Skip if filter data not loaded yet
    }

    const {validItems, filterString} = getValidItemsFromUrl(parameters, filter.filter, props);

    if (validItems.length > 0) {
      // If singleSelect is true, only use the first valid item
      const itemsToSelect = filter.singleSelect && validItems.length > 1
        ? [validItems[0]]
        : validItems;
      selectedFilters[`selected${filterString}s`] = itemsToSelect;
    }
  }

  // Add selectedDynamicFilters to result if any dynamic filters were found
  if (Object.keys(selectedDynamicFilters).length > 0) {
    selectedFilters.selectedDynamicFilters = selectedDynamicFilters;
  }

  return selectedFilters;
};

/**
 *
 * Given a filter, return the updated selected state and whether there are results.
 * Note: filteredX values are no longer returned here since they are computed dynamically
 * from selectedX values in the component's render method.
 *
 * @param {string} filter - Filter type
 * @param {Object} state - Component state object
 * @param {Object} props - Component props object
 * @returns {
 *     results: boolean,
 *     selectedFilter: Array
 * }
 */
export const updateFiltersFromUrl = (filter, state, props) => {
  const filterAlias = props.settings.urlParametersMap[filter]?.alias || filter
  const parameters = state.urlParameters ? state.urlParameters[filterAlias] : null;

  // if no parameters check if the filter is required
  if (!parameters) {
    if (props.settings.urlParametersMap[filter] && props.settings.urlParametersMap[filter].required) {
      return {
        results: false,
      };
    } else {
      // otherwise we still want results even if there are no parameters
      return {
        results: true,
      };
    }
  }

  const {validItems, filterString} = getValidItemsFromUrl(parameters, filter, props);

  return {
    results: Boolean(validItems.length),
    [`selected${filterString}s`]: validItems,
  };
};

